import type { QueryClient } from '@tanstack/react-query'
import queryClient from 'api/queryClient'
import type { Filter } from 'components/filters/useStreamFilters'
import { ordinalsStreamQueryFilter } from 'components/panels/ordinalsThreshold'
import { resetNaturalFeedSessionSnapshot } from 'lib/naturalFeedSession'
import { getQuestsQueryKey, serializeFilterKey } from 'refactor/hooks/api/useGetQuestsQueryKey'

export type FilterNavigationRefreshTarget = {
    exact: boolean
    queryKey: readonly unknown[]
    resetRetainedFeedSession: boolean
}

type FilterNavigationRefreshDependencies = {
    client?: Pick<QueryClient, 'invalidateQueries'>
    resetRetainedFeedSession?: (sessionKey: string) => void
}

export function getFilterNavigationRefreshTarget(
    filter: Filter,
): FilterNavigationRefreshTarget | null {
    if (!filter || filter.questId || filter.answerId) return null

    if (filter.streams) {
        return {
            exact: false,
            queryKey: ['streams'],
            resetRetainedFeedSession: false,
        }
    }

    if (filter.user_music) {
        return {
            exact: false,
            queryKey: ['radioArtist', filter.user_music],
            resetRetainedFeedSession: false,
        }
    }

    if (filter.memes) {
        return {
            exact: false,
            queryKey: ['memes'],
            resetRetainedFeedSession: false,
        }
    }

    const isOrdinalsMediaStream = !filter.search && Boolean(filter.ordinals || filter.user_ordinals)
    if (isOrdinalsMediaStream) {
        const queryFilter = ordinalsStreamQueryFilter(filter)
        return {
            exact: true,
            queryKey: ['ordinalsMediaStream', serializeFilterKey(queryFilter)],
            resetRetainedFeedSession: false,
        }
    }

    if (filter.history) {
        return {
            exact: false,
            queryKey: ['actions'],
            resetRetainedFeedSession: false,
        }
    }

    return {
        exact: true,
        queryKey: getQuestsQueryKey(filter, filter.notifications ? { clear: true } : {}),
        resetRetainedFeedSession: true,
    }
}

export function refreshFilterForNavigation(
    filter: Filter,
    dependencies: FilterNavigationRefreshDependencies = {},
): Promise<void> {
    const target = getFilterNavigationRefreshTarget(filter)
    if (!target) return Promise.resolve()

    if (target.resetRetainedFeedSession) {
        const sessionKey = target.queryKey[1]
        if (sessionKey != null) {
            const resetSession =
                dependencies.resetRetainedFeedSession || resetNaturalFeedSessionSnapshot
            resetSession(String(sessionKey))
        }
    }

    const client = dependencies.client || queryClient
    return client.invalidateQueries({
        exact: target.exact,
        queryKey: [...target.queryKey],
    })
}
